import React, { useMemo } from 'react';

import { Input } from 'antd';
import { Segmented } from 'antd';
import { isEmpty } from 'lodash';

import { getMoment, showDate, showDateNoTime } from '@libs/NwMoment';

import Money from '@components/Gui/Money';
import CustomSelect from '@components/Gui/NwForm/CustomSelect';
import NwIcon from '@components/Gui/NwIcon';
import PeriodSelector from '@components/Gui/PeriodSelector';

import { useStatementContext } from '../StatementContext';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const PeriodTotalBox = ({ date, periodBalances }) => {
    return (
        <div className="statement-period-total-box">
            <h4>balances at {date}</h4>
            {periodBalances ? (
                <>
                    <div className="statement-period-total-box-line statement-period-total-box-line-balance">
                        <p>Total:</p>
                        {Object.entries(periodBalances.Balances).map(([currency, amount]) => (
                            <div className="statement-period-total-box-line-currency">
                                <Money
                                    key={currency}
                                    amount={amount}
                                    currency={currency}
                                    redIfNegative
                                    boldIfDefaultCurrency
                                />
                            </div>
                        ))}
                    </div>
                    <div className="statement-period-total-box-line statement-period-total-box-line-available">
                        <p>Available:</p>
                        {Object.entries(periodBalances.AvailableBalances).map(([currency, amount]) => (
                            <div className="statement-period-total-box-line-currency">
                                <Money
                                    key={currency}
                                    amount={amount}
                                    currency={currency}
                                    redIfNegative
                                    boldIfDefaultCurrency
                                />
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <>
                    <div className="statement-period-total-box-line statement-period-total-box-line-balance">
                        <p>Total: /</p>
                    </div>
                    <div className="statement-period-total-box-line statement-period-total-box-line-available">
                        <p>Available: /</p>
                    </div>
                </>
            )}
        </div>
    );
};

/**
 * PayableNavigation
 *
 * Navigation bar for payable statements (period, filters, search)
 */
const PayableNavigation = ({
    billingInfoFilter,
    billingInfoOptions,
    endBalances,
    familiesFilter,
    handleChangeSearchTerm,
    handleClearSearchTerm,
    modelFilter,
    modelList,
    multipleBillingInfos,
    onUpdatePeriod,
    onUpdateRange,
    searchTerm,
    setBillingInfoFilter,
    setFamiliesFilter,
    setModelFilter,
    setStatementTypeFilter,
    startBalances,
    statementTypeFilter,
    transactionOptions,
}) => {
    const { filters, alwaysEnabled, contactType } = useStatementContext();

    const rangeAsMoment = useMemo(() => {
        return [getMoment(filters.range[0]), getMoment(filters.range[1])];
    }, [filters.range]);

    const dayBeforePeriod = useMemo(() => {
        const dBP = getMoment(filters.range[0]).subtract(1, 'day');
        if (dBP.year() > 1900) {
            return showDateNoTime(dBP, true);
        }
        return null;
    }, [filters.range]);

    const lastDayOfPeriod = useMemo(() => {
        return showDateNoTime(filters.range[1], true);
    }, [filters.range]);

    return (
        <>
            <div className="statement-navigation">
                <div style={{ flex: 1 }}>
                    <PeriodSelector
                        period={rangeAsMoment}
                        onChange={onUpdateRange}
                        alwaysEnabled={alwaysEnabled}
                        defaultOption={filters.period}
                        onChangePeriodOption={onUpdatePeriod}
                    />
                </div>
                {dayBeforePeriod && <PeriodTotalBox date={dayBeforePeriod} periodBalances={startBalances} />}
                {dayBeforePeriod && lastDayOfPeriod && (
                    <div className="statement-period-total-box-separator">
                        <NwIcon size="2x" icon={byPrefixAndName.fasl['arrow-right']} />
                    </div>
                )}
                {lastDayOfPeriod && <PeriodTotalBox date={lastDayOfPeriod} periodBalances={endBalances} />}
            </div>
            <div className="statement-navigation">
                <div className="statement-filter">
                    <small>Statement Type</small>
                    <Segmented
                        value={statementTypeFilter}
                        onChange={setStatementTypeFilter}
                        size="small"
                        options={[
                            { value: 'Real', label: 'Confirmed' },
                            { value: 'Hold', label: 'On Hold' },
                            { value: 'Previsional', label: 'Pending' },
                        ]}
                    />
                </div>
                {contactType === 'model' && transactionOptions.length > 0 && (
                    <CustomSelect
                        style={{ maxWidth: '15rem' }}
                        options={transactionOptions}
                        value={familiesFilter}
                        onChange={setFamiliesFilter}
                        optionLabelProp="data-label"
                    />
                )}
                {(contactType === 'agency' || contactType === 'service') && modelList.length > 0 && (
                    <CustomSelect
                        style={{ maxWidth: '15rem' }}
                        options={[{ value: null, label: 'All models' }, ...modelList]}
                        value={modelFilter}
                        onChange={setModelFilter}
                    />
                )}
                {multipleBillingInfos && (
                    <CustomSelect
                        // allowClear
                        dropdownMatchSelectWidth={false}
                        style={{ maxWidth: '15rem' }}
                        options={billingInfoOptions}
                        value={billingInfoFilter}
                        onChange={setBillingInfoFilter}
                        popupClassName="billing-info-popup"
                        optionLabelProp="data-label"
                    />
                )}
                <div className="statement-search">
                    <NwIcon icon={byPrefixAndName.fasl['search']} />
                    <Input
                        autoComplete="off"
                        value={searchTerm}
                        placeholder="Search transactions"
                        onChange={handleChangeSearchTerm}
                        allowClear={{
                            clearIcon: (
                                <NwIcon icon={byPrefixAndName.fass['circle-xmark']} onClick={handleClearSearchTerm} />
                            ),
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default PayableNavigation;
